// Sheet contract for the Pawn Customer Master bulk import: the column
// definitions, the downloadable template built from them, the parser, and the
// cross-row analysis that decides what gets created and what gets skipped.
//
// Kept free of React/MUI on purpose — BulkCustomerDialog.js owns the UI and the
// API calls, this module owns "what does the spreadsheet mean". COLUMNS is the
// single source of truth: it generates the template AND drives the parser, so
// the two can never drift out of order.
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export const TEMPLATE_FILE_NAME = "Pawn_Bulk_Customer_Template.xlsx";

// ─── Column contract ──────────────────────────────────────────────────────────
// The four nominee-control columns come first so a row reads left-to-right as
// "what is this, whose nominee is it, how are they related" before the person's
// own details. Everything after that is the New Customer form, in form order.
export const COLUMNS = [
  {
    letter: "A",
    header: "Type",
    key: "type",
    mandatory: false,
    whatToFill:
      "'customer' for a normal customer, 'nominee' for somebody who is a nominee of another row. Blank counts as 'customer'.",
    example: "customer",
    goesTo:
      "customer_master.cust_role — nominee rows also become customer_master rows, just linked through customer_nominees",
  },
  {
    letter: "B",
    header: "Parent Mobile",
    key: "parentMobile",
    mandatory: false,
    whatToFill:
      "NOMINEE ROWS ONLY: the mobile number of the customer this person is a nominee of. That customer can be another row in this sheet or somebody who already exists in the app. If more than one person shares that number, fill Parent Aadhar as well to say which one. Leave blank on customer rows.",
    example: "9876543210",
    goesTo: "customer_nominees.customer_id (resolved from the mobile number)",
  },
  {
    letter: "C",
    header: "Parent Aadhar",
    key: "parentAadhar",
    mandatory: false,
    whatToFill:
      "NOMINEE ROWS ONLY: the Aadhar number of the customer this person is a nominee of — matched against a customer row in this same sheet (their Aadhar Number column) or against anybody already in the app. Mainly needed when that customer's mobile is shared by more than one person. When filled it decides the customer and Parent Mobile is ignored, so you may fill either or both. Leave blank to go by Parent Mobile.",
    example: "234567890123",
    goesTo:
      "customer_nominees.customer_id (resolved from the Aadhar document, which pins one person down where a mobile cannot)",
  },
  {
    letter: "D",
    header: "Nominee Relation",
    key: "nomineeRelation",
    mandatory: false,
    whatToFill:
      "NOMINEE ROWS ONLY: how this person is related to their customer — Son, Wife, Brother, Friend… Free text. Leave blank on customer rows.",
    example: "Son",
    goesTo: "customer_nominees.relation — shown on the nominee card",
  },
  {
    letter: "E",
    header: "Party Code",
    key: "partyCode",
    mandatory: false,
    whatToFill:
      "Your own customer / party code from the old app. Leave blank to have none.",
    example: "PC-1042",
    goesTo: "customer_master.party_code — shown as 'Party Code' on the screen",
  },
  {
    letter: "F",
    header: "Name",
    key: "name",
    mandatory: true,
    whatToFill: "Full name. Saved in UPPERCASE, exactly like the screen does.",
    example: "RAMESH KUMAR",
    goesTo: "customer_master.name",
  },
  {
    letter: "G",
    header: "Mobile",
    key: "mobile",
    mandatory: true,
    whatToFill:
      "10-digit mobile number. Normally one number = one person and a repeat is skipped; if the store has 'Allow duplicate mobile numbers' switched on, repeats are allowed and people are told apart by Aadhar / PAN.",
    example: "9876543210",
    goesTo: "customer_master.mobile — also used as the Photo document number",
  },
  {
    letter: "H",
    header: "Date of Birth",
    key: "dob",
    mandatory: false,
    whatToFill:
      "DD/MM/YYYY, or a real Excel date cell, or YYYY-MM-DD. Blank is fine.",
    example: "14/08/1979",
    goesTo: "customer_master.dob — the screen shows the age beside it",
  },
  {
    letter: "I",
    header: "Relation Type",
    key: "relationType",
    mandatory: false,
    whatToFill:
      'This person\'s OWN S/o, D/o or W/o prefix ("son of" / "wife of" wording is understood too). Not the nominee relation — that is column D. Anything unrecognised becomes None.',
    example: "S/o",
    goesTo: "customer_master.relative_relation",
  },
  {
    letter: "J",
    header: "Relative Name",
    key: "relationName",
    mandatory: false,
    whatToFill: "Father's / husband's name. Saved in UPPERCASE.",
    example: "SURESH KUMAR",
    goesTo: "customer_master.relative_name",
  },
  {
    letter: "K",
    header: "Annual Income",
    key: "annualIncome",
    mandatory: false,
    whatToFill: "Plain number in rupees. Commas and a ₹ sign are ignored.",
    example: "250000",
    goesTo: "customer_master.annual_income",
  },
  {
    letter: "L",
    header: "Address",
    key: "address",
    mandatory: false,
    whatToFill:
      "Full address. Required when typing a customer on the screen, but a blank is ACCEPTED here so old records can be migrated as-is.",
    example: "12, Gandhi Bazaar, Basavanagudi, Bengaluru 560004",
    goesTo: "customer_master.pickup_address",
  },
  {
    letter: "M",
    header: "Note",
    key: "note",
    mandatory: false,
    whatToFill: "Any free remark you want carried over.",
    example: "Old app party code 1042, regular customer since 2016",
    goesTo: "customer_master.note",
  },
  {
    letter: "N",
    header: "PAN Number",
    key: "pan",
    mandatory: false,
    whatToFill:
      "PAN number only — the image cannot come from a spreadsheet. Format ABCDE1234F; a wrong format is still imported, just flagged.",
    example: "ABCDE1234F",
    goesTo: "documents row named 'PAN' (file saved as 'No file')",
  },
  {
    letter: "O",
    header: "Aadhar Number",
    key: "aadhar",
    mandatory: false,
    whatToFill: "12-digit Aadhar number only. Spaces are removed.",
    example: "234567890123",
    goesTo: "documents row named 'Aadhar' (file saved as 'No file')",
  },
  {
    letter: "P",
    header: "Other Document Number",
    key: "otherDoc",
    mandatory: false,
    whatToFill:
      "Ration card / voter ID / driving licence number, if you kept one. Creates the optional 'Other' document. Leave blank for none.",
    example: "KA0120110012345",
    goesTo: "documents row named 'Other' — only created when this is filled",
  },
  // Deliberately LAST, and it must stay last. A sheet whose header row is
  // unreadable falls back to being read strictly by POSITION, so inserting a
  // column anywhere else would silently shift every column after it for every
  // template a shop already downloaded and filled in. Appended, an older
  // 16-column sheet still maps A–P correctly and simply has no Q.
  {
    letter: "Q",
    header: "Old Customer ID",
    key: "oldCustomerId",
    mandatory: false,
    whatToFill:
      "The customer's ID number in your OLD software (its customer table's id). Copy it exactly. Fill this in and your old loans, receipts and settlements can be attached to the right person automatically — without it, they have to be matched by mobile number, which several people can share. Leave blank for someone who is new rather than migrated.",
    example: "412",
    goesTo: "customer_master.old_id",
  },
];

export const HEADERS = COLUMNS.map((c) => c.header);

// Column letter for a field, so error messages can name the column without
// anybody having to remember the order.
export const colOf = (key) => COLUMNS.find((c) => c.key === key)?.letter || "?";

// Sheets that must never be treated as data, whatever their position.
const NON_DATA_SHEETS = ["column guide", "notes", "sample data"];
export const DATA_SHEET_NAME = "Ready To Fill";

// How many columns must match by name before the first row counts as a header
// row rather than data. A real header matches every column; a data row that happens to
// hold a word equal to some column's name matches one or two.
const HEADER_MATCH_MINIMUM = Math.max(3, Math.ceil(COLUMNS.length / 3));

// Rows the template itself writes into the "Template" sheet. If somebody fills
// and uploads that sheet, these must not be mistaken for people.
const GUARD_PREFIXES = [
  "what to fill:",
  "example:",
  "mandatory:",
  "(excel column",
];

// A worked example to copy from: two customers, three nominees between them,
// and a customer with none. The last pair shows the Parent Aadhar column doing
// its job — two different people share the mobile 9845012399, so the nominee
// says which of them it belongs to by Aadhar.
//
// Written as named objects, not positional arrays: the row order is derived from
// COLUMNS below, so inserting a column can never silently shift the samples.
const SAMPLE_PEOPLE = [
  {
    type: "customer",
    partyCode: "PC-1042",
    name: "RAMESH KUMAR",
    oldCustomerId: "412",
    mobile: "9876543210",
    dob: "14/08/1979",
    relationType: "S/o",
    relationName: "SURESH KUMAR",
    annualIncome: "250000",
    address: "12, Gandhi Bazaar, Basavanagudi, Bengaluru 560004",
    note: "Old app party code 1042, regular customer since 2016",
    pan: "ABCDE1234F",
    aadhar: "234567890123",
    otherDoc: "KA0120110012345",
  },
  {
    type: "nominee",
    parentMobile: "9876543210",
    nomineeRelation: "Wife",
    name: "GEETHA BAI",
    oldCustomerId: "413",
    mobile: "9845012345",
    dob: "22/11/1983",
    relationType: "W/o",
    relationName: "RAMESH KUMAR",
    address: "12, Gandhi Bazaar, Basavanagudi, Bengaluru 560004",
    aadhar: "345678901234",
  },
  {
    type: "nominee",
    parentMobile: "9876543210",
    nomineeRelation: "Son",
    name: "SUNIL KUMAR",
    oldCustomerId: "414",
    mobile: "9845012346",
    dob: "09/03/2004",
    relationType: "S/o",
    relationName: "RAMESH KUMAR",
    address: "12, Gandhi Bazaar, Basavanagudi, Bengaluru 560004",
  },
  {
    type: "customer",
    partyCode: "PC-1043",
    name: "LAKSHMI DEVI",
    oldCustomerId: "508",
    mobile: "9845012399",
    dob: "02/02/1986",
    relationType: "W/o",
    relationName: "MOHAN RAO",
    annualIncome: "180000",
    address: "45/2, 3rd Cross, Jayanagar 4th Block, Bengaluru 560011",
    aadhar: "456789012345",
  },
  {
    // Shares LAKSHMI DEVI's phone, so this row would be ambiguous on the mobile
    // alone. Only a customer row needs a distinguishing Aadhar of its own.
    type: "customer",
    partyCode: "PC-1044",
    name: "MOHAN RAO",
    oldCustomerId: "509",
    mobile: "9845012399",
    dob: "17/09/1981",
    relationType: "S/o",
    relationName: "VENKAT RAO",
    address: "45/2, 3rd Cross, Jayanagar 4th Block, Bengaluru 560011",
    note: "Shares his wife's mobile number — told apart by Aadhar",
    aadhar: "567890123456",
  },
  {
    // Parent Aadhar picks LAKSHMI DEVI out of the two people on 9845012399.
    type: "nominee",
    parentMobile: "9845012399",
    parentAadhar: "456789012345",
    nomineeRelation: "Daughter",
    name: "DIVYA RAO",
    oldCustomerId: "510",
    mobile: "9731122334",
    dob: "05/05/2006",
    relationType: "D/o",
    relationName: "MOHAN RAO",
    address: "45/2, 3rd Cross, Jayanagar 4th Block, Bengaluru 560011",
  },
  {
    type: "customer",
    name: "ANITHA S",
    mobile: "9731122335",
    relationType: "D/o",
    relationName: "SRINIVAS",
    address: "8, Nehru Road, Mysuru 570001",
    note: "Brand new customer — no Old Customer ID because she was never in the old app",
  },
];

export const SAMPLE_ROWS = SAMPLE_PEOPLE.map((person) =>
  COLUMNS.map((c) => person[c.key] ?? ""),
);

const NOTES_LINES = [
  [
    `How this bulk import works`,
  ],
  [""],
  [
    `Screen: Pawn Customer Master > 'Bulk Create Customers' button`,
  ],
  [
    `Customers: one POST /api/pawn/customers per customer row — identical to saving one customer on the screen`,
  ],
  [
    `Nominees: one PUT /api/pawn/customers/<parent id> with nominees_to_add — identical to adding a nominee on the screen`,
  ],
  [""],
  [
    `WHAT GOES IN THIS SHEET`,
  ],
  [
    `1. Type your rows into the 'Ready To Fill' sheet. It already has the correct header row.`,
  ],
  [
    `2. The 'Sample Data' sheet is a full worked example — customers, nominees, and a couple who share one phone. It is NEVER imported; copy from it into 'Ready To Fill'.`,
  ],
  [
    `3. Upload as .xlsx, .xls or .csv. There is no need to convert the workbook to CSV.`,
  ],
  [
    `4. Columns are matched by header name when the header row is intact; if the headers are unrecognisable they are read by position (${colOf("type")} to ${colOf("otherDoc")}). So rename OR reorder — never both.`,
  ],
  [
    `5. MANDATORY on every row: Name (column ${colOf("name")}) and Mobile (column ${colOf("mobile")}). A row missing either is skipped and listed in the result.`,
  ],
  [
    `6. Address (column ${colOf("address")}) is required when typing a customer on the screen, but a blank is accepted here so older records migrate as-is.`,
  ],
  [
    `7. Leave a cell blank rather than leaving the column's own name in it. A cell still holding e.g. "PAN Number" is treated as blank and flagged.`,
  ],
  [""],
  [
    `CUSTOMERS AND NOMINEES IN ONE SHEET`,
  ],
  [
    `8. A nominee is a person too, so a nominee fills in exactly the same columns as a customer. Only Type, Parent Mobile, Parent Aadhar and Nominee Relation say that they are a nominee.`,
  ],
  [
    `9. Type = 'customer' (or blank) creates a normal customer. Type = 'nominee' creates the person AND links them to the customer named by Parent Mobile / Parent Aadhar.`,
  ],
  [
    `10. The order of rows does not matter — a nominee may appear above its customer. All customers are created first, then every nominee link is made.`,
  ],
  [
    `11. One person can be a nominee of several customers: repeat them as a nominee row per customer. They are created once and linked each time.`,
  ],
  [
    `12. A person can be both a customer and somebody's nominee: give them a customer row and a nominee row. They are created once, from the customer row.`,
  ],
  [
    `13. Nominee Relation (column ${colOf("nomineeRelation")}) is free text — Son, Wife, Brother, Friend. Optional, but it is what the nominee card shows.`,
  ],
  [""],
  [
    `NAMING A NOMINEE'S CUSTOMER`,
  ],
  [
    `14. Parent Mobile (column ${colOf("parentMobile")}) is the normal way: it must match exactly one customer, in this sheet or already in the app.`,
  ],
  [
    `15. Parent Aadhar (column ${colOf("parentAadhar")}) is the tie-breaker for stores where a mobile number is shared. When it is filled it decides the customer on its own and Parent Mobile is ignored, so you may fill either, or both. If the two point at different customers the Aadhar wins and the row is flagged.`,
  ],
  [
    `16. Parent Aadhar is matched BOTH against a customer row in this same sheet (their own Aadhar Number, column ${colOf("aadhar")}) and against customers already in the app — exactly like Parent Mobile. Either works.`,
  ],
  [
    `17. If Parent Mobile matches more than one person and Parent Aadhar is blank, the nominee row is skipped rather than attached to the wrong person. Fill Parent Aadhar to fix it.`,
  ],
  [""],
  [
    `DUPLICATE MOBILE NUMBERS`,
  ],
  [
    `18. By default one mobile number = one person. Two customer rows with the same mobile means the second is skipped, and the result tells you the row number and the number.`,
  ],
  [
    `19. If the store has 'Allow duplicate mobile numbers' switched on in Pawn Settings, repeats are allowed instead — a family sharing one phone is normal, and such stores tell people apart by Aadhar / PAN. The bulk import then offers a switch to create them; every shared number is still listed so a genuine double entry stays visible.`,
  ],
  [
    `20. A customer row whose mobile already exists in the app is skipped unless duplicates are allowed. Nothing is ever overwritten — the import never edits an existing customer.`,
  ],
  [
    `21. A NOMINEE row whose mobile matches exactly one existing person links that person and leaves their details untouched. If it matches several, a new record is created rather than guessing which one.`,
  ],
  [
    `22. The same nominee listed twice for the same customer is skipped the second time — one link is all that is needed.`,
  ],
  [
    `23. Party codes (column ${colOf("partyCode")}) are different: they carry a real per-store unique index, so a repeat ALWAYS fails. Those rows are skipped whatever the duplicate-mobile setting says. Leave the column blank if you have no codes.`,
  ],
  [""],
  [
    `OLD CUSTOMER ID — the column that makes migrating your loans possible`,
  ],
  [
    `24. Old Customer ID (column ${colOf("oldCustomerId")}) is the customer's id number in your OLD software. It is not shown anywhere in the app; it exists purely so your old loans, receipts and settlements can find the right person.`,
  ],
  [
    `25. FILL IT IN if you are going to import your old loans. Your old software records a loan against a customer NUMBER, and that number means nothing here — every customer imported gets a fresh id of its own. This column is what ties the two together.`,
  ],
  [
    `26. Without it, a loan has to be matched to its customer by mobile number, and a number shared by two people cannot say which of them the loan belongs to. Those loans are then reported instead of imported.`,
  ],
  [
    `27. Copy the id exactly as your old software shows it. It is treated as text, so 412 and 0412 are two different customers — which is right, because that is how your old records read them.`,
  ],
  [
    `28. A row whose Old Customer ID is already in the app is SKIPPED and left untouched, so re-uploading a part-finished sheet is safe. Leave the column blank for anyone who is genuinely new rather than migrated.`,
  ],
  [""],
  [
    `EVERYTHING ELSE`,
  ],
  [
    `24. Mobile must end up as exactly 10 digits. A leading 0 or a 91 country code is stripped for you; anything still not 10 digits is skipped.`,
  ],
  [
    `25. Dates accept DD/MM/YYYY, YYYY-MM-DD, or a real Excel date cell. A date that cannot be read skips the row rather than saving a wrong birthday.`,
  ],
  [
    `26. OTP verification is NOT applied to a bulk import, even when 'OTP for customer' is switched on in Pawn Settings — 100 OTPs cannot be collected.`,
  ],
  [
    `27. Document IMAGES cannot come from a spreadsheet. Only the PAN / Aadhar / Other NUMBERS are imported; open the person on the screen afterwards to attach images.`,
  ],
  [
    `28. Every row is created under the store you are currently logged into.`,
  ],
  [
    `29. Nothing is created until you press the Create button. Rows are created one at a time, so if the import is interrupted the rows already created stay created.`,
  ],
];

// ─── Template workbook ────────────────────────────────────────────────────────
const autoFitCols = (rows, min = 10, max = 60) =>
  (rows[0] || []).map((_, colIdx) => ({
    wch: Math.min(
      max,
      Math.max(min, ...rows.map((r) => String(r?.[colIdx] ?? "").length + 2)),
    ),
  }));

export const downloadCustomerTemplate = () => {
  const wb = XLSX.utils.book_new();

  // 1. "Template" — headers with the documentation stacked underneath each one,
  //    so the answer to "what goes in this column?" sits in the column itself.
  const templateRows = [
    HEADERS,
    COLUMNS.map((c) => `WHAT TO FILL: ${c.whatToFill}`),
    COLUMNS.map((c) => `EXAMPLE: ${c.example}`),
    COLUMNS.map((c) => `MANDATORY: ${c.mandatory ? "YES" : "No"}`),
    COLUMNS.map((c) => `(Excel column ${c.letter})`),
  ];
  const wsTemplate = XLSX.utils.aoa_to_sheet(templateRows);
  wsTemplate["!cols"] = autoFitCols([HEADERS], 16, 28);
  XLSX.utils.book_append_sheet(wb, wsTemplate, "Template");

  // 2. "Column Guide" — the same contract as a readable table.
  const guideRows = [
    [
      "Excel Column",
      "Header",
      "What to fill",
      "Example",
      "Mandatory",
      "Where this data goes",
    ],
    ...COLUMNS.map((c) => [
      c.letter,
      c.header,
      c.whatToFill,
      c.example,
      c.mandatory ? "YES" : "No",
      c.goesTo,
    ]),
  ];
  const wsGuide = XLSX.utils.aoa_to_sheet(guideRows);
  wsGuide["!cols"] = [
    { wch: 13 },
    { wch: 24 },
    { wch: 78 },
    { wch: 40 },
    { wch: 11 },
    { wch: 62 },
  ];
  XLSX.utils.book_append_sheet(wb, wsGuide, "Column Guide");

  // 3. "Notes" — the rules of the import.
  const wsNotes = XLSX.utils.aoa_to_sheet(NOTES_LINES);
  wsNotes["!cols"] = [{ wch: 155 }];
  XLSX.utils.book_append_sheet(wb, wsNotes, "Notes");

  // 4. "Sample Data" — a worked example to copy from. Never imported.
  const sampleRows = [HEADERS, ...SAMPLE_ROWS];
  const wsSample = XLSX.utils.aoa_to_sheet(sampleRows);
  wsSample["!cols"] = autoFitCols(sampleRows, 12, 50);
  XLSX.utils.book_append_sheet(wb, wsSample, "Sample Data");

  // 5. "Ready To Fill" — header row only, so the first data row is row 2.
  const wsFill = XLSX.utils.aoa_to_sheet([HEADERS]);
  wsFill["!cols"] = autoFitCols(sampleRows, 12, 50);
  XLSX.utils.book_append_sheet(wb, wsFill, DATA_SHEET_NAME);

  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([buffer], { type: "application/octet-stream" }),
    TEMPLATE_FILE_NAME,
  );
};

// ─── Cell helpers ─────────────────────────────────────────────────────────────
export const txt = (v) =>
  v === null || v === undefined ? "" : String(v).trim();

// A record's key in the shop's OLD software, normalised. Text, never a number
// — the old chart of accounts proves those keys can be alphanumeric, and the
// trailing-".0" strip is what makes an Excel number cell ("412.0") match the
// same ID typed by hand ("412"). Must stay identical to normalizeOldId in
// pawnImportController.js and oldKey in importData/importRunner.js.
export const oldId = (v) => {
  const raw = txt(v);
  if (!raw) return null;
  return raw.replace(/\.0+$/, "").toUpperCase();
};

const normKey = (v) =>
  String(v ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
const pad2 = (n) => String(n).padStart(2, "0");

export const normalizeMobile = (v) => {
  let digits = txt(v).replace(/\D/g, "");
  if (digits.length === 13 && digits.startsWith("091"))
    digits = digits.slice(3);
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  return digits;
};

// Row kind. Blank counts as a customer so a sheet that has no nominees at all
// can leave the Type column empty throughout.
export const normalizeType = (v) => {
  const key = normKey(v);
  if (!key) return "customer";
  if (/^(n|nom|nominee|nominees)$/.test(key)) return "nominee";
  if (/^(c|cust|customer|customers|primary|main)$/.test(key)) return "customer";
  return null; // unrecognised — the caller turns this into a skip
};

const RELATION_MAP = [
  { match: /^(s\/?o|son|sonof|sof)$/, value: "S/o" },
  { match: /^(d\/?o|daughter|daughterof|dof)$/, value: "D/o" },
  { match: /^(w\/?o|wife|wifeof|wof)$/, value: "W/o" },
];

export const normalizeRelation = (v) => {
  const key = normKey(v);
  if (!key) return "None";
  const hit = RELATION_MAP.find((r) => r.match.test(key));
  return hit ? hit.value : "None";
};

// Accepts a real Excel date cell, an Excel serial number, DD/MM/YYYY and
// YYYY-MM-DD. Returns { ok, value, reason } — value is an ISO date or null, and
// `reason` says which way it went wrong so the row can explain itself.
export const toISODate = (v) => {
  if (v === null || v === undefined || txt(v) === "")
    return { ok: true, value: null };

  const UNREADABLE = "could not be read — write it as DD/MM/YYYY";

  const build = (y, m, d) => {
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    const isRealDate =
      date.getFullYear() === Number(y) &&
      date.getMonth() === Number(m) - 1 &&
      date.getDate() === Number(d);
    if (!isRealDate)
      return { ok: false, reason: "is not a real calendar date" };
    if (Number(y) < 1900) return { ok: false, reason: "is before 1900" };
    if (date > new Date()) return { ok: false, reason: "is in the future" };
    return { ok: true, value: `${y}-${pad2(m)}-${pad2(d)}` };
  };

  if (v instanceof Date)
    return isNaN(v.getTime())
      ? { ok: false, reason: UNREADABLE }
      : build(v.getFullYear(), v.getMonth() + 1, v.getDate());

  if (typeof v === "number" && isFinite(v)) {
    const parsed = XLSX.SSF?.parse_date_code?.(v);
    if (parsed?.y) return build(parsed.y, parsed.m, parsed.d);
    return { ok: false, reason: UNREADABLE };
  }

  const s = txt(v);
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/); // YYYY-MM-DD
  if (m) return build(m[1], m[2], m[3]);

  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/); // DD/MM/YYYY
  if (m) {
    let year = m[3];
    if (year.length === 2) year = Number(year) > 40 ? `19${year}` : `20${year}`;
    return build(year, m[2], m[1]);
  }

  return { ok: false, reason: UNREADABLE };
};

const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

// ─── Sheet → rows ─────────────────────────────────────────────────────────────
// Picks the sheet to read, drops the header/guide rows, and keeps the true Excel
// row number of everything that survives so problems can be pointed at.
export const extractRows = (workbook) => {
  const candidates = workbook.SheetNames.filter(
    (n) => !NON_DATA_SHEETS.includes(n.trim().toLowerCase()),
  );
  const lower = (n) => n.trim().toLowerCase();
  const ordered = [
    ...candidates.filter((n) => lower(n) === DATA_SHEET_NAME.toLowerCase()),
    ...candidates.filter((n) => lower(n) === "template"),
    ...candidates.filter(
      (n) =>
        lower(n) !== DATA_SHEET_NAME.toLowerCase() && lower(n) !== "template",
    ),
  ];

  for (const sheetName of ordered) {
    const ws = workbook.Sheets[sheetName];
    if (!ws || !ws["!ref"]) continue;

    const firstRow = XLSX.utils.decode_range(ws["!ref"]).s.r;
    const aoa = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      blankrows: true,
      raw: true,
      defval: null,
    });

    let headerIndexes = null; // column key -> position, when headers are readable
    let headerRowConsidered = false;
    const dataRows = [];

    aoa.forEach((row, idx) => {
      const cells = Array.isArray(row) ? row : [];
      const rowNo = firstRow + idx + 1; // 1-based, as Excel shows it

      if (cells.every((c) => txt(c) === "")) return;

      const firstFilled = txt(cells.find((c) => txt(c) !== ""));
      if (GUARD_PREFIXES.some((p) => firstFilled.toLowerCase().startsWith(p)))
        return;

      // The header can only be the FIRST non-empty, non-guard row, and it has to
      // look like a real header — a clear majority-ish of the columns matching by
      // name. Both halves matter: a person who leaves the placeholder text "Note"
      // and "PAN Number" sitting in those cells produces a data row that matches
      // two headers, and a looser rule swallowed every such row as a header and
      // reported the file as empty.
      if (!headerRowConsidered) {
        headerRowConsidered = true;
        const found = {};
        cells.forEach((cell, colIdx) => {
          const hit = COLUMNS.find((c) => normKey(c.header) === normKey(cell));
          if (hit && found[hit.key] === undefined) found[hit.key] = colIdx;
        });
        if (Object.keys(found).length >= HEADER_MATCH_MINIMUM) {
          headerIndexes = found;
          return;
        }
      }

      dataRows.push({ rowNo, cells });
    });

    if (dataRows.length) return { sheetName, headerIndexes, dataRows };
  }

  return { sheetName: null, headerIndexes: null, dataRows: [] };
};

// ─── Rows → per-row records ───────────────────────────────────────────────────
// Only row-local validation happens here (blank name, unreadable date…).
// Anything needing the other rows or the app's data is left to analyseRecords.
export const buildRecords = ({ headerIndexes, dataRows }) =>
  dataRows.map(({ rowNo, cells }) => {
    // Cells left holding their own column name — someone copied the header row
    // down, or typed over only some of it. Importing "PAN Number" as a PAN
    // number is never what they meant, so treat it as blank and say so.
    const placeholders = [];

    const get = (key) => {
      const column = COLUMNS.find((c) => c.key === key);

      // Header mode and positional mode are never mixed within a row. Once the
      // header row is understood it is the only authority: a column missing from
      // it is genuinely absent, and must NOT fall back to a positional guess.
      // Mixing them broke every sheet downloaded before a column was inserted —
      // an older 15-column sheet had "Nominee Relation" sitting at the position
      // Parent Aadhar now occupies, so its nominee rows died on
      // `Parent Aadhar "Son" has no digits in it`.
      const idx = headerIndexes
        ? headerIndexes[key] ?? -1
        : COLUMNS.findIndex((c) => c.key === key);
      if (idx < 0) return null;

      const cell = cells[idx];
      if (column && normKey(cell) && normKey(cell) === normKey(column.header)) {
        placeholders.push(column.header);
        return null;
      }
      return cell;
    };

    const errors = [];
    const warnings = [];

    const kind = normalizeType(get("type"));
    if (kind === null)
      errors.push(
        `Type "${txt(
          get("type"),
        )}" is not understood — use 'customer' or 'nominee' (column ${colOf(
          "type",
        )})`,
      );

    const name = txt(get("name")).toUpperCase();
    if (!name) errors.push(`Name is blank (column ${colOf("name")})`);

    const rawMobile = txt(get("mobile"));
    const mobile = normalizeMobile(rawMobile);
    if (!mobile) errors.push(`Mobile is blank (column ${colOf("mobile")})`);
    else if (mobile.length !== 10)
      errors.push(`"${rawMobile}" is not a 10-digit mobile number`);

    const parentMobile = normalizeMobile(get("parentMobile"));
    const rawParent = txt(get("parentMobile"));
    if (rawParent && !parentMobile)
      errors.push(`Parent Mobile "${rawParent}" is not a usable mobile number`);

    // The tie-breaker for when a customer's mobile is shared by several people.
    const rawParentAadhar = txt(get("parentAadhar"));
    const parentAadhar = rawParentAadhar.replace(/\D/g, "");
    if (rawParentAadhar && !parentAadhar)
      errors.push(
        `Parent Aadhar "${rawParentAadhar}" has no digits in it (column ${colOf("parentAadhar")})`,
      );

    const nomineeRelation = txt(get("nomineeRelation"));

    if (kind === "customer") {
      const strays = [];
      if (parentMobile) strays.push("Parent Mobile");
      if (parentAadhar) strays.push("Parent Aadhar");
      if (nomineeRelation) strays.push("Nominee Relation");
      if (strays.length)
        warnings.push(
          `${strays.join(", ")} ${
            strays.length === 1 ? "is" : "are"
          } only used on nominee rows — ignored here`,
        );
    }

    const dobCell = get("dob");
    const dobResult = toISODate(dobCell);
    if (!dobResult.ok) {
      const shown =
        dobCell instanceof Date && !isNaN(dobCell.getTime())
          ? dobCell.toDateString()
          : txt(dobCell);
      errors.push(`Date of Birth "${shown}" ${dobResult.reason}`);
    }

    const address = txt(get("address"));
    if (!address)
      warnings.push("No address — the screen normally requires one");

    const rawIncome = txt(get("annualIncome")).replace(/[₹,\s]/g, "");
    let annualIncome = null;
    if (rawIncome) {
      const parsed = Number(rawIncome);
      if (!isFinite(parsed))
        errors.push(
          `Annual Income "${txt(get("annualIncome"))}" is not a number`,
        );
      else annualIncome = parsed;
    }

    const relationRaw = txt(get("relationType"));
    const relation = normalizeRelation(relationRaw);
    if (relationRaw && relation === "None")
      warnings.push(
        `Relation Type "${relationRaw}" not recognised — saved as None`,
      );

    const pan = txt(get("pan"))
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
    if (pan && !PAN_PATTERN.test(pan))
      warnings.push(
        `PAN "${pan}" is not in ABCDE1234F format — imported as typed`,
      );

    const aadhar = txt(get("aadhar")).replace(/\D/g, "");
    if (aadhar && aadhar.length !== 12)
      warnings.push(`Aadhar "${aadhar}" is not 12 digits — imported as typed`);

    const otherDoc = txt(get("otherDoc"));

    // Same document set the screen builds: PAN / Aadhar / Photo always go up
    // (the API skips the ones with no number), "Other" only when filled in.
    const documents = [
      { name: "PAN", number: pan, file: null, fileName: "", renewalDate: null },
      {
        name: "Aadhar",
        number: aadhar,
        file: null,
        fileName: "",
        renewalDate: null,
      },
      {
        name: "Photo",
        number: "",
        file: null,
        fileName: "",
        renewalDate: null,
      },
    ];
    if (otherDoc)
      documents.push({
        name: "Other",
        number: otherDoc,
        file: null,
        fileName: "",
        renewalDate: null,
      });

    // The person's own fields, shared by the POST body and by nominees_to_add.
    //
    // old_id is normalised the same way everywhere it is read or written —
    // trim, strip a trailing ".0", upper-case. The ".0" matters: an ID column
    // in Excel is a NUMBER cell, and xlsx hands 412 back as "412" from one
    // file and "412.0" from the next. Unnormalised, the same customer imports
    // twice. Keep this in step with normalizeOldId in pawnImportController.js
    // and oldKey in importData/importRunner.js.
    const person = {
      party_code: txt(get("partyCode")) || null,
      old_id: oldId(get("oldCustomerId")),
      name,
      mobile,
      dob: dobResult.value,
      pickup_address: address || null,
      relative_relation: relation,
      relative_name: txt(get("relationName")).toUpperCase() || null,
      annual_income: annualIncome,
      note: txt(get("note")) || null,
      documents,
    };

    // Built here, after every get() has run, so the list is complete.
    if (placeholders.length)
      warnings.push(
        `${placeholders.join(", ")} still held the column name instead of a value — left blank`,
      );

    return {
      rowNo,
      cells,
      kind: kind || "customer",
      name,
      mobile,
      parentMobile,
      parentAadhar,
      nomineeRelation,
      // This person's own Aadhar, lifted out of `person.documents` so a nominee
      // in the same sheet can name its customer by it.
      aadhar,
      errors,
      warnings,
      person,
    };
  });

// Every non-blank party code the import needs to check for availability.
// Upper-cased because the store's codes are matched case-insensitively here.
export const partyCodesToVerify = (records) => {
  const set = new Set();
  records.forEach((r) => {
    if (r.errors.length) return;
    const code = txt(r.person?.party_code);
    if (code) set.add(code.toUpperCase());
  });
  return [...set];
};

// Every mobile the import needs to look up in the app before it can decide
// anything: each person's own, plus every parent referenced.
export const mobilesToVerify = (records) => {
  const set = new Set();
  records.forEach((r) => {
    if (r.errors.length) return;
    if (r.mobile) set.add(r.mobile);
    if (r.kind === "nominee" && r.parentMobile) set.add(r.parentMobile);
  });
  return [...set];
};

// Every Aadhar used to identify a nominee's parent. Only nominee rows can name
// one — a customer row's own Aadhar is just a document, not a lookup key.
export const parentAadharsToVerify = (records) => {
  const set = new Set();
  records.forEach((r) => {
    if (r.errors.length) return;
    if (r.kind === "nominee" && r.parentAadhar) set.add(r.parentAadhar);
  });
  return [...set];
};

// ─── Cross-row analysis ─────────────────────────────────
// Turns per-row records into the two work lists (customers to create, nominee
// links to make) plus everything thrown out and why. Row order is irrelevant:
// customers are collected first, so a nominee may sit above its customer.
//
// Options, all describing what the store already holds:
//   existingByMobile    Map<mobile, [{id,name}]> — every cust_role. A mobile can
//                       hold several people once the store allows that.
//   existingPartyCodes  Map<UPPERCASE code, {id,name}>
//   existingByAadhar    Map<aadhar, [{id,name}]> — for parents named by Aadhar
//   existingOldIds      Map<normalised old id, {id,name,status}> — old-app ids
//                       already migrated into this store
//   allowDuplicateMobile  the store's setting: when true a repeated mobile is a
//                       warning rather than a skip
export const analyseRecords = (
  records,
  {
    existingByMobile = new Map(),
    existingPartyCodes = new Map(),
    existingByAadhar = new Map(),
    existingOldIds = new Map(),
    allowDuplicateMobile = false,
  } = {},
) => {
  const skipped = [];
  const skip = (r, reason, category) =>
    skipped.push({
      rowNo: r.rowNo,
      cells: r.cells,
      name: r.name,
      mobile: r.mobile,
      kind: r.kind,
      reason,
      category,
    });

  const usable = [];
  records.forEach((r) => {
    if (r.errors.length) skip(r, r.errors.join(" · "), "invalid");
    else usable.push(r);
  });

  const holdersOf = (mobile) => existingByMobile.get(mobile) || [];
  const describe = (people) => people.map((h) => `"${h.name}"`).join(", ");

  // Party codes are unique per store — uq_store_party_code (store_id,
  // party_code) — so the same code twice in one sheet, or one already taken in
  // the app, is a guaranteed API failure. Catching it here keeps the pre-flight
  // honest instead of promising a row that is certain to fail. A blank code is
  // always fine: it is stored as NULL, and a unique index ignores NULLs.
  //
  // Registers the code as a side effect, so call it once per row and only as the
  // last check before the row is accepted.
  const partyCodeSeen = new Map(); // UPPER-CASED code -> row that claimed it
  const partyCodeClash = (r) => {
    const code = txt(r.person?.party_code);
    if (!code) return null;
    const key = code.toUpperCase();

    const twin = partyCodeSeen.get(key);
    if (twin)
      return `Duplicate party code "${code}" — already used by "${twin.name}" on row ${twin.rowNo}`;

    const held = existingPartyCodes.get(key);
    if (held)
      return `Party code "${code}" already belongs to "${held.name}" in the app`;

    partyCodeSeen.set(key, r);
    return null;
  };

  // Old Customer ID carries its own per-store unique index —
  // uq_customer_old_id (store_id, old_source, old_id) — so exactly like a party
  // code, a repeat in the sheet or one already in the app is a certain API
  // failure. Unlike a party code, though, a clash here usually means something
  // reassuring: this customer HAS already been migrated, so re-running a
  // half-finished sheet is safe and the message says so.
  //
  // Blank is always fine (stored NULL, and a unique index ignores NULLs), which
  // is what lets a brand-new customer sit in the same sheet as migrated ones.
  //
  // Registers the id as a side effect — call once per row, last.
  const oldIdSeen = new Map(); // normalised old id -> row that claimed it
  const oldIdClash = (r) => {
    const key = oldId(r.person?.old_id);
    if (!key) return null;

    const twin = oldIdSeen.get(key);
    if (twin)
      return `Old Customer ID "${key}" appears twice in this sheet — row ${twin.rowNo} ("${twin.name}") already used it`;

    const held = existingOldIds.get(key);
    if (held)
      return held.status === "A"
        ? `Old Customer ID "${key}" is already in the app as "${held.name}" — this person has been imported already`
        : `Old Customer ID "${key}" is held by "${held.name}", a customer that was deleted. Restore them, or clear this cell to import a fresh record.`;

    oldIdSeen.set(key, r);
    return null;
  };

  // ── Customers ────────────────────────────────────────────────
  const customers = [];
  const sheetCustomers = new Map(); // mobile -> [records]; several when shared
  const sheetCustomersByAadhar = new Map(); // aadhar -> [records]
  usable
    .filter((r) => r.kind === "customer")
    .forEach((r) => {
      const twins = sheetCustomers.get(r.mobile) || [];
      const inApp = holdersOf(r.mobile);
      const extraWarnings = [];

      if (!allowDuplicateMobile) {
        if (twins.length) {
          skip(
            r,
            `Duplicate mobile ${r.mobile} — already used by "${twins[0].name}" on row ${twins[0].rowNo}`,
            "duplicate-mobile",
          );
          return;
        }
        if (inApp.length) {
          skip(
            r,
            `Mobile ${r.mobile} already belongs to "${inApp[0].name}" in the app — nothing was changed`,
            "duplicate-mobile",
          );
          return;
        }
      } else if (twins.length || inApp.length) {
        // Allowed, but never silent: name who else is on the number so a genuine
        // double entry is still noticeable.
        const others = [
          ...twins.map((t) => `"${t.name}" on row ${t.rowNo}`),
          ...inApp.map((h) => `"${h.name}" already in the app`),
        ];
        extraWarnings.push(
          `Mobile ${r.mobile} is shared with ${others.join(", ")} — tell them apart by Aadhar or PAN`,
        );
      }

      // Before the party code: an already-migrated customer is the more useful
      // thing to be told, and it is the reason a re-run of a part-finished
      // sheet is safe.
      const oldClash = oldIdClash(r);
      if (oldClash) {
        skip(r, oldClash, "already-imported");
        return;
      }

      const partyClash = partyCodeClash(r);
      if (partyClash) {
        skip(r, partyClash, "duplicate-party");
        return;
      }

      const enriched = { ...r, warnings: [...r.warnings, ...extraWarnings] };
      sheetCustomers.set(r.mobile, [...twins, enriched]);
      // Indexed by Aadhar as well, so a nominee may name its customer by Aadhar
      // even when that customer is only a row in this same sheet. Customers are
      // all collected before any nominee is looked at, so this is complete by
      // the time it is read.
      if (r.aadhar)
        sheetCustomersByAadhar.set(r.aadhar, [
          ...(sheetCustomersByAadhar.get(r.aadhar) || []),
          enriched,
        ]);
      customers.push(enriched);
    });

  // ── Nominee links ─────────────────────────────────────────────
  const nominees = [];
  const linkSeen = new Map(); // "parentKey>nomineeMobile" -> row that claimed it
  usable
    .filter((r) => r.kind === "nominee")
    .forEach((r) => {
      if (!r.parentMobile && !r.parentAadhar) {
        skip(
          r,
          `Nominee row names no customer — fill Parent Mobile (column ${colOf(
            "parentMobile",
          )}) or Parent Aadhar (column ${colOf("parentAadhar")})`,
          "invalid",
        );
        return;
      }

      // Aadhar wins when given: it pins one person down where a shared mobile
      // cannot. Only when it is blank does the mobile decide.
      let parentInSheet = null;
      let parentInApp = null;
      // Local, never pushed onto the record: the analysis is re-run whenever the
      // duplicate switch is flipped, and a mutated record would stack warnings.
      const parentNotes = [];

      if (r.parentAadhar) {
        // Searched in this sheet as well as in the app, exactly like Parent
        // Mobile is — a customer usually arrives on an earlier row of the very
        // same sheet, and looking only in the app made Parent Aadhar useless in
        // that (most common) case.
        const inSheet = sheetCustomersByAadhar.get(r.parentAadhar) || [];
        const inApp = existingByAadhar.get(r.parentAadhar) || [];
        const total = inSheet.length + inApp.length;

        if (!total) {
          skip(
            r,
            `No customer holds Parent Aadhar ${r.parentAadhar} — neither a customer row in this sheet (their Aadhar Number, column ${colOf(
              "aadhar",
            )}) nor anybody in the app`,
            "invalid",
          );
          return;
        }
        if (total > 1) {
          skip(
            r,
            `Parent Aadhar ${r.parentAadhar} matches ${total} customers (${describe(
              [...inSheet, ...inApp],
            )}) — it cannot say which one`,
            "invalid",
          );
          return;
        }
        if (inSheet.length) parentInSheet = inSheet[0];
        else parentInApp = inApp[0];

        // Both keys given but pointing at different people: the Aadhar decides,
        // as documented, but a contradiction is worth seeing.
        if (r.parentMobile) {
          const byMobile = [
            ...(sheetCustomers.get(r.parentMobile) || []),
            ...holdersOf(r.parentMobile),
          ];
          const chosen = parentInSheet || parentInApp;
          const mobileAgrees = byMobile.some((c) =>
            parentInSheet ? c.rowNo === chosen.rowNo : c.id === chosen.id,
          );
          if (byMobile.length && !mobileAgrees)
            parentNotes.push(
              `Parent Mobile ${r.parentMobile} and Parent Aadhar ${r.parentAadhar} point at different customers — the Aadhar was used, so this is a nominee of "${chosen.name}"`,
            );
        }
      } else {
        const inSheet = sheetCustomers.get(r.parentMobile) || [];
        const inApp = holdersOf(r.parentMobile);
        const total = inSheet.length + inApp.length;

        if (!total) {
          skip(
            r,
            `Parent mobile ${r.parentMobile} is not a customer row in this sheet and does not exist in the app`,
            "invalid",
          );
          return;
        }
        if (total > 1) {
          skip(
            r,
            `Parent mobile ${r.parentMobile} matches ${total} people (${describe([
              ...inSheet,
              ...inApp,
            ])}) — put that customer's Aadhar in Parent Aadhar (column ${colOf(
              "parentAadhar",
            )}) to say which one`,
            "invalid",
          );
          return;
        }
        if (inSheet.length) parentInSheet = inSheet[0];
        else parentInApp = inApp[0];
      }

      if (parentInSheet && parentInSheet.rowNo === r.rowNo) {
        skip(
          r,
          "This row is its own customer — nobody can be their own nominee",
          "invalid",
        );
        return;
      }
      if (!r.parentAadhar && r.parentMobile === r.mobile) {
        skip(
          r,
          "Parent Mobile is this person's own number — nobody can be their own nominee",
          "invalid",
        );
        return;
      }

      const parentKey = parentInSheet
        ? `row${parentInSheet.rowNo}`
        : `app${parentInApp.id}`;
      const linkKey = `${parentKey}>${r.mobile}`;
      const twin = linkSeen.get(linkKey);
      if (twin) {
        skip(
          r,
          `Duplicate nominee — ${r.mobile} is already a nominee of the same customer on row ${twin.rowNo}`,
          "duplicate-link",
        );
        return;
      }
      linkSeen.set(linkKey, r);

      const warnings = [...r.warnings, ...parentNotes];
      const alsoCustomer = (sheetCustomers.get(r.mobile) || [])[0];
      const alreadyInApp = holdersOf(r.mobile);
      if (alsoCustomer)
        warnings.push(
          `Also customer row ${alsoCustomer.rowNo} — created once there, then linked as a nominee here`,
        );
      else if (alreadyInApp.length === 1)
        warnings.push(
          `${r.mobile} already exists in the app as "${alreadyInApp[0].name}" — that person is linked, their own details are left untouched`,
        );
      else if (alreadyInApp.length > 1)
        warnings.push(
          `${r.mobile} is shared by ${alreadyInApp.length} people in the app — a new record is created for this nominee rather than guessing which one`,
        );

      // Only a nominee who is a brand-new person gets a customer_master row of
      // their own, so only then can their party code clash. When the person
      // already exists (as a sheet customer, or as the single holder of that
      // mobile) nominees_to_add just links them and never touches their code.
      // Only when this nominee is a brand-new person. If they already exist,
      // nominees_to_add just links them and never touches their own record —
      // so neither their party code nor their old id is being claimed here.
      const willCreateNewPerson = !alsoCustomer && alreadyInApp.length !== 1;
      if (willCreateNewPerson) {
        const oldClash = oldIdClash(r);
        if (oldClash) {
          skip(r, oldClash, "already-imported");
          return;
        }

        const partyClash = partyCodeClash(r);
        if (partyClash) {
          skip(r, partyClash, "duplicate-party");
          return;
        }
      }

      nominees.push({
        ...r,
        warnings,
        parentName: parentInSheet?.name || parentInApp?.name || r.parentMobile,
        parentRowNo: parentInSheet?.rowNo ?? null,
        parentExistingId: parentInSheet ? null : parentInApp.id,
        // Pins the nominee's own identity for the API when a mobile cannot.
        nomineeExistingId:
          alreadyInApp.length === 1 && !alsoCustomer ? alreadyInApp[0].id : null,
      });
    });

  return { customers, nominees, skipped };
};

// ─── Failure / skip report ────────────────────────────────────────────────────
// Exports the untouched cells of every row that did not make it, so the user can
// fix those rows and re-upload just them.
export const downloadRowReport = (rows, fileName) => {
  if (!rows?.length) return;
  const aoa = [
    [...HEADERS, "Excel Row", "What happened"],
    ...rows.map((r) => [
      ...COLUMNS.map((_, idx) => {
        const cell = r.cells?.[idx];
        return cell instanceof Date && !isNaN(cell.getTime())
          ? cell.toISOString().slice(0, 10)
          : txt(cell);
      }),
      r.rowNo,
      r.reason,
    ]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = autoFitCols(aoa, 12, 60);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Rows Not Imported");
  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
};
